import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from "@angular/core";
import { FmsService } from "src/app/services/fms.service";
import { RadioButton, RadioButtonsModule } from "@storeblocks/radio-buttons-ng";
import { memoizeObject$ } from "src/app/utils/rxjs/select";
import { combineLatest, map, Observable, Subject, takeUntil } from "rxjs";
import { getIsNotNullable, Nullable, stringToBoolean } from "src/app/utils/utils";
import { ConsentLabels } from "src/app/modules/shared/components/onboarding/onboarding-public-pension-step/onboarding-public-pension-step-radio-creator";
import { FormControl, Validators } from "@angular/forms";

import { Router } from "@angular/router";
import { ConsentService } from "src/app/services/consent.service";
import { tap } from "rxjs/operators";
import { AccordionModule } from "@storeblocks/accordion-ng";
import { FmsPipe } from "src/app/modules/shared/pipes/fms.pipe";
import { FormatTextForConsentPipe } from "src/app/modules/shared/pipes/format-text-for-consent.pipe";
import { AsyncPipe } from "@angular/common";
import { ButtonModule } from "@storeblocks/button-ng";
import { ModalModule } from "@storeblocks/modal-ng";
import { DialogRef } from "src/app/services/dialog/dialog-ref";
import { DIALOG_DATA } from "src/app/services/dialog/dialog.service";

enum CONSENT_KEYS {
  GROUP,
  MARKETING,
}
interface DialogData {
  url: string;
}

@Component({
  selector: "app-onboarding-group-and-marketing-consents",
  templateUrl: "./group-and-marketing-consents.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RadioButtonsModule,
    AccordionModule,
    FmsPipe,
    FormatTextForConsentPipe,
    AsyncPipe,
    ButtonModule,
    ModalModule,
  ],
})
export class GroupAndMarketingConsentsComponent implements OnDestroy {
  public steps = [
    {
      step: CONSENT_KEYS.GROUP,
      fmsKey: "consents.button.continue",
    },
    {
      step: CONSENT_KEYS.MARKETING,
      fmsKey: "consents.button.saveAndNext",
    },
  ];
  public step = CONSENT_KEYS.GROUP;
  public consentKeys = CONSENT_KEYS;

  public groupRadioButtons$!: Observable<Array<RadioButton>>;
  public marketingElectronicRadioButtons$!: Observable<Array<RadioButton>>;
  public marketingSocialMediaRadioButtons$!: Observable<Array<RadioButton>>;
  public groupConsentControl: FormControl = new FormControl(null, Validators.required);
  public marketingElectronicConsentControl: FormControl = new FormControl(null, Validators.required);
  public marketingSocialMediaConsentControl: FormControl = new FormControl(null, Validators.required);
  public isSubmitting = false;
  private readonly destroy$ = new Subject<void>();
  private readonly consentLabels$ = memoizeObject$(
    combineLatest([
      this.fmsService.translateAsync<string>("onboardingConsents.yes"),
      this.fmsService.translateAsync<string>("onboardingConsents.no"),
    ]).pipe(
      map(([yesLabel, noLabel]) => ({
        yesLabel,
        noLabel,
      })),
    ),
  );

  constructor(
    @Inject(DIALOG_DATA) public readonly data: DialogData,
    private readonly dialogRef: DialogRef<GroupAndMarketingConsentsComponent>,
    private readonly fmsService: FmsService,
    private readonly router: Router,
    private readonly consentService: ConsentService,
  ) {
    this.consentService.groupAndMarketingConsents$.pipe(takeUntil(this.destroy$)).subscribe((consents) => {
      this.createGroupRadioButtons(consents.group);
      this.marketingElectronicConsentControl.setValue(consents.marketingElectronic);
      this.marketingSocialMediaConsentControl.setValue(consents.marketingSocialMedia);
    });
  }

  public goNext(): void {
    if (this.step === CONSENT_KEYS.GROUP) {
      if (this.groupConsentControl.invalid) {
        this.groupConsentControl.markAsTouched();
        return;
      }
      this.step = CONSENT_KEYS.MARKETING;
      this.createMarketingRadioButtons(
        this.marketingElectronicConsentControl.value,
        this.marketingSocialMediaConsentControl.value,
      );
    } else if (this.step === CONSENT_KEYS.MARKETING) {
      if (this.marketingElectronicConsentControl.invalid) {
        this.marketingElectronicConsentControl.markAsTouched();
      }
      if (this.marketingSocialMediaConsentControl.invalid) {
        this.marketingSocialMediaConsentControl.markAsTouched();
      }

      if (this.marketingElectronicConsentControl.invalid || this.marketingSocialMediaConsentControl.invalid) {
        return;
      }

      this.isSubmitting = true;
      this.consentService
        .patchGroupAndMarketingConsent(
          this.groupConsentControl.value,
          this.marketingElectronicConsentControl.value,
          this.marketingSocialMediaConsentControl.value,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.dialogRef.close();
            this.isSubmitting = false;
            this.router.navigate([this.data.url]);
          },
          () => {
            this.isSubmitting = false;
          },
        );
    }
  }

  public goBack(): void {
    this.step = CONSENT_KEYS.GROUP;
    this.createGroupRadioButtons(this.groupConsentControl.value);
  }

  public setControlValue(formControl: FormControl, item: RadioButton): void {
    const booleanValue = stringToBoolean(item.value);
    formControl.markAsTouched();
    formControl.setValue(booleanValue);
  }

  public close(): void {
    this.dialogRef.close();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createGroupRadioButtons(checked: boolean | undefined): void {
    this.groupRadioButtons$ = this.consentLabels$.pipe(
      map((consentLabels) => this.createConsentRadioButtons(checked, consentLabels)),
      tap(() => {
        this.groupConsentControl.setValue(checked);
      }),
    );
  }

  private createMarketingRadioButtons(
    electronicChecked: boolean | undefined,
    socialMediaChecked: boolean | undefined,
  ): void {
    this.marketingElectronicRadioButtons$ = this.consentLabels$.pipe(
      map((consentLabels) => this.createConsentRadioButtons(electronicChecked, consentLabels)),
      tap(() => {
        this.marketingElectronicConsentControl.setValue(electronicChecked);
      }),
    );
    this.marketingSocialMediaRadioButtons$ = this.consentLabels$.pipe(
      map((consentLabels) => this.createConsentRadioButtons(socialMediaChecked, consentLabels)),
      tap(() => {
        this.marketingSocialMediaConsentControl.setValue(socialMediaChecked);
      }),
    );
  }

  private createConsentRadioButtons(
    constentValue: Nullable<boolean> = undefined,
    labels: ConsentLabels,
  ): Array<RadioButton> {
    const hasConsentValue = getIsNotNullable(constentValue);

    return [
      {
        label: labels.yesLabel,
        value: true.toString(),
        disabled: false,
        checked: hasConsentValue ? constentValue : false,
      },
      {
        label: labels.noLabel,
        value: false.toString(),
        disabled: false,
        checked: hasConsentValue ? !constentValue : false,
      },
    ];
  }
}
